import { ComponentProps } from 'react';
import styled, { css, ThemeProps } from 'styled-components';
import { Theme } from '../theme';
import { font, media } from '../theme/base/mixins';
import { StyledDivComponent } from '../types';
import { ButtonAnchor, ButtonLink } from './Button';
import { CardRectBody } from './CardRect';
import { Column, Row } from './Grid';
import { Subtitle } from './Subtitle';
import { StyledTitleH1 } from '../containers/SearchContainer/SearchDetail/SearchDetailHead'

const tableTitleStyles = css`
  .title {
    margin-bottom: 10px;
    color: ${(props: ThemeProps<Theme>) => props.theme.colors.secondary};
    font-size: 2.6rem;

    ${media.large`
      color: ${(props: ThemeProps<Theme>) => props.theme.colors.grayMedium};
      font-size: 3rem;
    `};

    @media print {
      font-size: 2rem;
      border-bottom: 1px solid
        ${(props: ThemeProps<Theme>) => props.theme.colors.gray};
    }
  }
`;

export const DetailRow: StyledDivComponent<ComponentProps<typeof Row>> = styled(
  Row
)`
  padding: 12px;
  position: relative;

  @media print {
    padding: 3px 0;
  }

  @media not print {
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 12px;
      height: 1px;
      background: ${(props: ThemeProps<Theme>) => props.theme.colors.gray};
      width: calc(100% - 24px);
    }
  }
`;

export const DetailPrice = styled.div`
  margin-top: 20px;

  ${StyledTitleH1}, ${Subtitle.H2} {
    display: inline-block;
  }

  ${media.large`
    margin-top: 0;
    text-align: right;

    ${StyledTitleH1},
    ${Subtitle.H2} {
      text-align: right;
      display: block;
    }
  `};

  .tax {
    ${font(1.8, 2.4, 'normal')};
  }

  @media print {
    margin-top: 0;
  }
`;

export const DetailLabel: StyledDivComponent<
  ComponentProps<typeof Column>
> = styled(Column)`
  ${font(1.8, 2.1, '300')};

  color: ${(props: ThemeProps<Theme>) => props.theme.colors.grayDark};
`;

export const DetailValue = styled(Column)`
  ${font(1.8, 2.1, '500')};

  ${media.medium`
    font-size: 2rem;
  `}
`;

export const DetailEquipmentTable: StyledDivComponent = styled.div`
  margin: 50px 0 80px;

  @media print {
    margin: 20px 0 20px;
  }

  ${tableTitleStyles}

  @media print {
    ${DetailValue} {
      ${font(1.8, 2.1, '300')};
    }
  }
`;

export const DetailHead: StyledDivComponent = styled.div`
  margin: 0 0 30px;

  @media print {
    margin-bottom: 20px;
  }

  .offer-info {
    font-size: 1.4rem;
    line-height: 1.15;
    text-align: right;
    color: ${(props: ThemeProps<Theme>) => props.theme.colors.grayMedium};
    margin: 0 0 24px;

    strong {
      font-weight: normal;
    }
  }

  .summary {
    span {
      margin: 0 19px 0 0;
    }
  }

  ${media.large`
    .offer-info {
      color: inherit;
      font-size: 1.8rem;

      strong {
        font-weight: bold;
      }
    }
  `};
`;

export const LocationDetails = styled(CardRectBody)`
  padding-top: 30px;
  padding-bottom: 30px;
  margin-bottom: 30px;

  .large-flex-container.large-flex-dir-column & {
    margin-bottom: 0;
  }

  @media print {
    padding: 0;
    flex: inherit;
    width: 50%;
    float: left;
  }
`;

export const DetailDataTable: StyledDivComponent = styled.div`
  margin: 50px 0 80px;

  @media print {
    margin: 20px 0 20px;

    .columns:last-child:not(:first-child) {
      float: left;
    }
  }

  ${tableTitleStyles}

  ${DetailRow} {
    height: 100%;
  }
`;

export const DetailSaveAs = styled.div`
  ${ButtonAnchor} {
    margin: 5px 0 30px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const SearchDetailNavigationContainer: StyledDivComponent = styled.div`
  margin-top: 20px;
  text-align: center;

  ${media.large`
    text-align: right;
    margin-top: 0;
  `}

  ${ButtonLink} {
    margin-left: 4px;
    margin-right: 4px;
    color: ${(props: ThemeProps<Theme>) => props.theme.colors.secondary};

    &:hover {
      color: ${(props: ThemeProps<Theme>) => props.theme.colors.white};
      background-color: ${(props: ThemeProps<Theme>) =>
        props.theme.colors.secondary};
    }

    ${media.large`
      margin-left: 8px;
      margin-right: 0;
    `};
  }
`;
